import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { Form, checkBox, constantField } from "trader.ts/ui.forms";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  studentemailid,
  studentid,
  teacherid,
} from "../../../sql/classy/types";
import * as style from "../../../styles/style.css.classes";
import { renderSaveButton } from "../../client/savebutton";
import { renderFirstNameLastName } from "../../render";
import * as routes from "../../routes";

type opts = {
  secret: string;
  students: {
    student_id: studentid;
    firstname: string;
    lastname: string;
    emails:
      | {
          studentemailid: studentemailid;
          email: string;
          wantsnotificationsformessages: boolean;
          wantsnotificationsfornotes: boolean;
        }[]
      | null;
  }[];
  teachers: {
    firstname: string;
    lastname: string;
    id: teacherid;
    email: string;
    wantsnotificationsformessages: boolean;
    wantsnotificationsfornotes: boolean;
    wantsenrollmentnotifications: boolean;
    wantsabsencerequestnotifications: boolean;
  }[];
  admin_accounts: {
    email: string;
    wantsnotificationsformessages: boolean;
    wantsccfornotes: boolean;
    wantsccformessages: boolean;
    sawwelcome: boolean;
    wantsenrollmentnotifications: boolean;
    wantsabsencerequestnotifications: boolean;
  }[];
};

export function render(opts: opts): HTMLElement | HTMLElement[] {
  function renderTitle(s: string) {
    return (
      <article>
        <h2>{s}</h2>
      </article>
    );
  }

  function renderStudentForm(stu: {
    firstname: string;
    lastname: string;
    email: string;
    studentemailid: studentemailid;
    wantsnotificationsformessages: boolean;
    wantsnotificationsfornotes: boolean;
  }) {
    const form = new Form({})
      .addField("tag", [], () => constantField("student" as const))
      .addField("emailaddress", [], () => constantField(stu.email))
      .addField("studentemailid", [], () => constantField(stu.studentemailid))
      .addField("secret", [], () => constantField(opts.secret))
      .addField("wantsnotificationsformessages", [], () =>
        checkBox({
          initialVal: stu.wantsnotificationsformessages,
          label: translate("wantsnotificationsformessages"),
          class: style.checkbox,
        }),
      )
      .addField("wantsnotificationsfornotes", [], () =>
        checkBox({
          initialVal: stu.wantsnotificationsfornotes,
          label: translate("wantsnotificationsfornotes"),
          class: style.checkbox,
        }),
      )
      .build();
    return (
      <section className={style.card + style.card_accent + style.card_large}>
        {renderTitle(
          "%email - Student %name"
            .replace("%email", stu.email)
            .replace("%name", renderFirstNameLastName(stu)),
        )}
        <article>{form.render()}</article>
        <div style="text-align: right">
          {renderSaveButton({
            formS: form.s,
            save: (f) => rpc(routes.saveEmailSettingsSpec, undefined, f),
            reload: false,
          })}
        </div>
      </section>
    );
  }

  function renderTeacherForm(teacher: opts["teachers"][number]) {
    const form = new Form({})
      .addField("tag", [], () => constantField("teacher" as const))
      .addField("emailaddress", [], () => constantField(teacher.email))
      .addField("teacherid", [], () => constantField(teacher.id))
      .addField("secret", [], () => constantField(opts.secret))
      .addField("wantsnotificationsformessages", [], () =>
        checkBox({
          initialVal: teacher.wantsnotificationsformessages,
          label: translate("wantsnotificationsformessages"),
          class: style.checkbox,
        }),
      )
      .addField("wantsnotificationsfornotes", [], () =>
        checkBox({
          initialVal: teacher.wantsnotificationsfornotes,
          label: translate("wantsnotificationsfornotes"),
          class: style.checkbox,
        }),
      )
      .addField("wantsenrollmentnotifications", [], () =>
        checkBox({
          initialVal: teacher.wantsenrollmentnotifications,
          label: translate("wantsenrollmentnotifications"),
          class: style.checkbox,
        }),
      )
      .addField("wantsabsencerequestnotifications", [], () =>
        checkBox({
          initialVal: teacher.wantsabsencerequestnotifications,
          label: translate("wantsabsencerequestnotifications"),
          class: style.checkbox,
        }),
      )
      .build();
    return (
      <section className={style.card + style.card_accent + style.card_large}>
        {renderTitle(
          "%email - Docent %name"
            .replace("%email", teacher.email)
            .replace("%name", renderFirstNameLastName(teacher)),
        )}
        <article>{form.render()}</article>
        <div style="text-align: right">
          {renderSaveButton({
            formS: form.s,
            save: (f) => rpc(routes.saveEmailSettingsSpec, undefined, f),
            reload: false,
          })}
        </div>
      </section>
    );
  }

  function renderAdminForm(admin: opts["admin_accounts"][number]) {
    const form = new Form({})
      .addField("tag", [], () => constantField("admin" as const))
      .addField("emailaddress", [], () => constantField(admin.email))
      .addField("secret", [], () => constantField(opts.secret))
      .addField("wantsnotificationsformessages", [], () =>
        checkBox({
          initialVal: admin.wantsnotificationsformessages,
          label: translate("wantsnotificationsformessages"),
          class: style.checkbox,
        }),
      )
      .addField("wantsccfornotes", [], () =>
        checkBox({
          initialVal: admin.wantsccfornotes,
          label: translate("wantsccfornotes"),
          class: style.checkbox,
        }),
      )
      .addField("wantsccformessages", [], () =>
        checkBox({
          initialVal: admin.wantsccformessages,
          label: translate("wantsccformessages"),
          class: style.checkbox,
        }),
      )
      .addField("wantsenrollmentnotifications", [], () =>
        checkBox({
          initialVal: admin.wantsenrollmentnotifications,
          label: translate("wantsenrollmentnotifications"),
          class: style.checkbox,
        }),
      )
      .addField("wantsabsencerequestnotifications", [], () =>
        checkBox({
          initialVal: admin.wantsabsencerequestnotifications,
          label: translate("wantsabsencerequestnotifications"),
          class: style.checkbox,
        }),
      )
      .build();
    return (
      <section className={style.card + style.card_accent + style.card_large}>
        {renderTitle("%email - Admin".replace("%email", admin.email))}
        <article>{form.render()}</article>
        <div style="text-align: right">
          {renderSaveButton({
            formS: form.s,
            save: (f) => rpc(routes.saveEmailSettingsSpec, undefined, f),
            reload: false,
          })}
        </div>
      </section>
    );
  }

  return (
    <div>
      {opts.admin_accounts.map(renderAdminForm)}

      {opts.teachers.orderBy(renderFirstNameLastName).map(renderTeacherForm)}

      {opts.students.orderBy(renderFirstNameLastName).flatMap((stu) =>
        (stu.emails || [])
          .orderBy((em) => em.email)
          .map((em) =>
            renderStudentForm({
              firstname: stu.firstname,
              lastname: stu.lastname,
              email: em.email,
              studentemailid: em.studentemailid,
              wantsnotificationsformessages: em.wantsnotificationsformessages,
              wantsnotificationsfornotes: em.wantsnotificationsfornotes,
            }),
          ),
      )}
    </div>
  );
}

function translate(
  cas:
    | "wantsnotificationsformessages"
    | "wantsnotificationsfornotes"
    | "wantsenrollmentnotifications"
    | "wantsabsencerequestnotifications"
    | "wantsccfornotes"
    | "wantsccformessages",
): string {
  if (cas === "wantsnotificationsformessages") {
    return "Stuur mij een email wanneer er een bericht naar mij wordt gestuurd";
  } else if (cas === "wantsnotificationsfornotes") {
    return "Stuur mij een email wanneer iemand een notitie schrijft bij een van mijn lessen";
  } else if (cas === "wantsenrollmentnotifications") {
    return "Stuur mij een email wanneer een student een nieuwe inschrijvingsaanvraag maakt";
  } else if (cas === "wantsabsencerequestnotifications") {
    return "Stuur mij een email wanneer een student een afwezigheid registreert";
  } else if (cas === "wantsccfornotes") {
    return "Stuur mij een email wanneer er een notitie wordt gemaakt bij een les";
  } else if (cas === "wantsccformessages") {
    return "Stuur mij een email wanneer er een bericht naar een docent of student wordt gestuurd";
  } else {
    return checkAllCasesHandled(cas);
  }
}
